import type { ReactNode } from "react";
import { Card, Text } from "../../elements";
import { CalendarIcon, OdometerIcon, PinIcon } from "../../icons";
import { currencyFormatter, odometerFormatter } from "../../support";
import type { Optional } from "../../types";
import { VehicleCardImageRecommendationDesktop } from "./vehicle-card-recommendation-image-desktop.component";
import styles from "./vehicle-card-recommendation.module.css";

export type VehicleCardRecommendationImageProps = {
  height: number;
  width: number;
  className: string;
  src: string;
  alt: string;
  title: string;
};

export type VehicleCardRecommendationProps = {
  title: string;
  description: string;
  price: string;
  location: string;
  odometer: string;
  image?: Optional<string>;
  noPhoto?: Optional<string>;
  year?: Optional<string>;
  url?: string;
  imageWidth?: number;
  onImageRender?: Optional<
    (props: VehicleCardRecommendationImageProps) => ReactNode
  >;
  target?: Optional<string>;
};

export function VehicleCardRecommendation({
  ...props
}: Readonly<VehicleCardRecommendationProps>) {
  const {
    title,
    price,
    image,
    odometer,
    location,
    description,
    year,
    url,
    imageWidth = 242,
    onImageRender,
    noPhoto,
    target,
  } = props;

  const photo = image || noPhoto;

  return (
    <div
      className={`${styles.Container} ${imageWidth ? styles.ImageWidth : ""}`}
      style={
        {
          "--image-width": `${imageWidth}px`,
        } as React.CSSProperties
      }
    >
      <Card>
        <Card.Head>
          <VehicleCardImageRecommendationDesktop
            title={`${title} ${description}`}
            onImageRender={onImageRender}
            src={photo!}
            imageWidth={imageWidth}
          />
        </Card.Head>

        <Card.Body>
          <div className={styles.Body}>
            <div className={styles.BodyContent}>
              <div>
                <a
                  className={styles.Link}
                  href={url}
                  target={target ?? "_blank"}
                  rel="nofollow"
                >
                  <Text
                    variant="web-title-medium"
                    color="#2E2D37"
                    className={styles.Title}
                  >
                    {title}
                  </Text>
                </a>
                <Text color="#696977" className={styles.Subtitle}>
                  {description}
                </Text>
              </div>
              <div className={styles.Cell}>
                <div className={styles.CellItem}>
                  <CalendarIcon color="#696977" size={18} viewBox="0 0 18 18" />
                  <Text color="#696977">{year}</Text>
                </div>
                <div className={styles.CellItem}>
                  <OdometerIcon color="#696977" size={18} viewBox="0 0 18 18" />
                  <Text color="#696977">{odometerFormatter(odometer)}</Text>
                </div>
              </div>

              <div className={styles.BodyItem}>
                <PinIcon size={18} viewBox="0 0 18 18" />
                <Text color="#696977" className={styles.Location}>
                  {location}
                </Text>
              </div>
              <div className={styles.BodyItem}>
                <Text variant="body-bold-large" color="#2E2D37">
                  {currencyFormatter(price)}
                </Text>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
